// Noxara Store: a Store Box (itens presos ao personagem: loja, recompensas, eventos) e a loja de donate.
// Visual de loja premium (azul-real + dourado). CSS em client/css-parts/store.css (prefixo .st-).
import { useState } from 'preact/hooks';
import type { ItemInstance } from '../../../../shared/src/game/items';
import { STORE_INBOX_SIZE } from '../../../../shared/src/game/items';
import { EXERCISE_KINDS, STORE_OFFERS } from '../../../../shared/src/game/store';
import { ItemImg, ItemSlot } from './ItemSlot';
import type { DragApi } from './ItemSlot';

const n0 = (v: number) => v.toLocaleString('pt-BR');
const KIND_TAG: Record<string, string> = { vip: 'VIP', bpass: 'Passe', exercise: 'Treino', item: 'Roleta', prey3: 'Prey', preycards: 'Prey' };

export function Store(props: { items: ItemInstance[]; api: DragApi; coins: number; vip: boolean; tab?: string; onBuy: (offer: string, exercise?: string) => void; extraTabs?: Array<[string, string, () => preact.ComponentChildren]> }) {
  const [tab, setTab] = useState<string>(props.tab ?? 'box');
  const [kind, setKind] = useState('sword');
  const [help, setHelp] = useState(false);
  const box = props.items.filter((i) => i.place === 'inbox');
  const at = (n: number) => box.find((i) => i.slot === String(n));
  const shown = Math.min(STORE_INBOX_SIZE, Math.max(20, Math.ceil((box.length + 5) / 5) * 5));
  const tabs: Array<[string, string]> = [['box', 'Store Box'], ['shop', 'Loja'], ...(props.extraTabs ?? []).map(([id, label]) => [id, label] as [string, string])];
  const extra = props.extraTabs?.find(([id]) => id === tab);
  const fill = Math.min(100, (box.length / STORE_INBOX_SIZE) * 100);
  return (
    <div class="st">
      <div class="st-head">
        <div class="st-crest" aria-hidden="true"><ItemImg typeId={22118} count={100} /></div>
        <div class="st-head-txt">
          <div class="st-title">Noxara Store <button class={'st-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <small>Ofertas premium, recompensas e a sua Store Box.</small>
        </div>
        <div class="st-wallet" title="Saldo de Noxara Coins">
          <span class="st-coinbig" aria-hidden="true" />
          <span class="st-bal"><small>Noxara Coins</small><b>{n0(props.coins)}</b></span>
          <span class={'st-vip' + (props.vip ? ' on' : '')} title={props.vip ? 'VIP ativo' : 'Sem VIP'}>{props.vip ? '👑 VIP' : 'Sem VIP'}</span>
        </div>
      </div>
      {help && (
        <div class="st-steps">
          <div><i>🪙</i><b>1. Escolha</b><small>Na Loja, cada oferta mostra o preço em Noxara Coins. O botão fica apagado se faltar saldo.</small></div>
          <div><i>📦</i><b>2. Store Box</b><small>Tudo o que você compra ou ganha (diária, passe, roleta, eventos) chega na Store Box.</small></div>
          <div><i>🎒</i><b>3. Use</b><small>Arraste o item da Store Box para a mochila. Ele fica preso ao personagem.</small></div>
        </div>
      )}
      <div class="st-nav">{tabs.map(([id, label]) => <button key={id} class={'tb-button' + (tab === id ? ' green' : '')} onClick={() => setTab(id)}>{label}</button>)}</div>
      {tab === 'box' && (
        <div class="st-box">
          <div class="st-cap">
            <span class="st-cap-l">📦 <b>{box.length}</b> de {STORE_INBOX_SIZE} espaços</span>
            <span class={'st-bar' + (fill >= 90 ? ' full' : '')}><i style={{ width: `${fill}%` }} /></span>
          </div>
          <p class="st-note">Itens da loja, recompensas e eventos chegam aqui. <b>Arraste para a sua mochila</b> para usar.
            Eles ficam presos ao personagem: não podem ser vendidos, trocados, jogados fora nem guardados no depot.</p>
          <div class="st-grid-wrap">
            <div class="depot-grid">
              {Array.from({ length: shown }, (_, n) => <ItemSlot key={n} item={at(n)} target={{ place: 'inbox', slot: n }} api={props.api} />)}
            </div>
          </div>
          {box.length === 0 && <p class="st-empty">A Store Box está vazia. Compre na Loja ou pegue a recompensa diária.</p>}
        </div>
      )}
      {tab === 'shop' && (
        <div class="st-shop">
          <div class="st-offers">
            {STORE_OFFERS.map((o) => {
              const short = o.price - props.coins;
              return (
                <div key={o.id} class={'st-offer k-' + o.kind + (short > 0 ? ' short' : '')}>
                  {KIND_TAG[o.kind] && <span class="st-tag">{KIND_TAG[o.kind]}</span>}
                  <div class="st-offer-top">
                    <span class="st-slot"><span class="item-slot">{o.img ? <img class="store-img" src={o.img} alt="" /> : <ItemImg typeId={o.icon} count={1} />}</span></span>
                    <div class="st-offer-txt"><b>{o.name}</b><small>{o.desc}</small></div>
                  </div>
                  <div class="st-offer-buy">
                    {o.kind === 'exercise' && (
                      <select class="st-select" value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value)}>
                        {EXERCISE_KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                    )}
                    {short > 0 && <span class="st-short">Faltam {n0(short)}</span>}
                    <button class="tb-button green st-price" disabled={props.coins < o.price} onClick={() => props.onBuy(o.id, o.kind === 'exercise' ? kind : undefined)}>
                      <i class="st-coin" />{n0(o.price)} coins</button>
                  </div>
                </div>
              );
            })}
          </div>
          <p class="st-note">As compras vão para a Store Box.</p>
        </div>
      )}
      {extra && <div class="st-extra">{extra[2]()}</div>}
    </div>
  );
}
